import { useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, CalendarClock, CornerDownLeft, Search } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { visibleSections } from '@/app/navigation'
import { useT } from '@/internationalization/i18n'
import { useUiStore, useSessionStore } from '@/stores'
import { useDebouncedValue, useEscapeKey, useHotkey, useLockBodyScroll } from '@/hooks'
import { api } from '@/internationalization/api-client'
import { formatDayLabel } from '@/internationalization/format'
import type { Meeting } from '@/types'

interface Command {
  id: string
  label: string
  hint?: string
  group: string
  run: () => void
}

/**
 * ⌘K / Ctrl-K. Navigation and meeting lookup in one keystroke.
 *
 * Two design points that matter:
 *  - Arrow keys move a highlighted index and Enter runs it. A palette that
 *    requires the mouse is a worse menu.
 *  - Meeting results are fetched on a debounced query, but navigation commands
 *    filter locally and appear instantly, so the palette never feels blocked on
 *    the network for the thing it is used for most.
 */
export function CommandPalette() {
  const t = useT()
  const isOpen = useUiStore((s) => s.isCommandPaletteOpen)
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen)
  const permissions = useSessionStore((s) => s.permissions)
  const canAccessVocabulary = useSessionStore((s) => s.canAccessVocabulary)
  const canAccessFeedbackInbox = useSessionStore((s) => s.canAccessFeedbackInbox)
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [meetings, setMeetings] = useState<Meeting[]>([])
  const debounced = useDebouncedValue(query, 200)

  useHotkey('k', () => setOpen(true), { meta: true, allowInInput: true })
  useEscapeKey(() => setOpen(false), isOpen)
  useLockBodyScroll(isOpen)

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setMeetings([])
      setActiveIndex(0)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || debounced.trim().length < 2) return
    let cancelled = false
    api
      .get<{ items: Meeting[] }>('/meetings', { query: { q: debounced, pageSize: 5 } })
      .then((result) => {
        if (!cancelled) setMeetings(result.items)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [debounced, isOpen])

  const commands = useMemo<Command[]>(() => {
    const term = query.trim().toLowerCase()
    // Same gate the sidebar applies -- otherwise a permission-gated page is
    // invisible in the nav and one Cmd+K query away regardless.
    const navCommands: Command[] = visibleSections(
      permissions, canAccessVocabulary, canAccessFeedbackInbox,
    ).flatMap((section) =>
      section.items.map((item) => ({
        id: `nav-${item.id}`,
        label: t('palette.goTo', { label: t(`nav.item.${item.id}`) }),
        hint: item.shortcut,
        group: t('palette.group.navigate'),
        run: () => navigate(item.to),
      })),
    ).filter((command) => !term || command.label.toLowerCase().includes(term))

    const meetingCommands: Command[] = meetings.map((meeting) => ({
      id: `meeting-${meeting.id}`,
      label: meeting.title,
      hint: formatDayLabel(meeting.startsAt),
      group: t('palette.group.meetings'),
      run: () => navigate(`/meetings/${meeting.id}`),
    }))

    const searchCommand: Command[] = term
      ? [
          {
            id: 'full-search',
            label: t('palette.searchFor', { term: query.trim() }),
            group: t('palette.group.search'),
            run: () => navigate(`/search?q=${encodeURIComponent(query.trim())}`),
          },
        ]
      : []

    return [...searchCommand, ...meetingCommands, ...navCommands]
  }, [query, meetings, navigate, t, permissions, canAccessVocabulary, canAccessFeedbackInbox])

  if (!isOpen) return null

  const run = (command: Command) => {
    command.run()
    setOpen(false)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % Math.max(1, commands.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (index - 1 + commands.length) % Math.max(1, commands.length))
    } else if (event.key === 'Enter' && commands[activeIndex]) {
      event.preventDefault()
      run(commands[activeIndex])
    }
  }

  let renderedGroup = ''

  return (
    <div className="fixed inset-0 z-modal flex items-start justify-center px-3 pt-[10vh]">
      <div className="absolute inset-0 animate-fade-in bg-black/50" onClick={() => setOpen(false)} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.dialogLabel')}
        className="relative w-full max-w-xl animate-scale-in overflow-hidden rounded-xl border border-border
          bg-surface-raised shadow-lg"
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search aria-hidden className="h-4 w-4 shrink-0 text-fg-subtle" />
          <input
            autoFocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={onKeyDown}
            placeholder={t('palette.placeholder')}
            aria-label={t('palette.inputAria')}
            className="h-12 w-full bg-transparent text-md text-fg placeholder:text-fg-subtle focus:outline-none"
          />
          <kbd className="hidden rounded border border-border bg-surface-muted px-1.5 py-0.5 text-2xs text-fg-subtle sm:block">
            esc
          </kbd>
        </div>

        <ul className="max-h-[60vh] overflow-y-auto py-2">
          {commands.length === 0 && (
            <li className="px-4 py-6 text-center text-base text-fg-muted">{t('palette.noMatches')}</li>
          )}
          {commands.map((command, index) => {
            const showGroup = command.group !== renderedGroup
            renderedGroup = command.group
            return (
              <li key={command.id}>
                {showGroup && (
                  <p className="px-4 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wide text-fg-subtle">
                    {command.group}
                  </p>
                )}
                <button
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => run(command)}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-4 py-2 text-left text-base',
                    index === activeIndex ? 'bg-brand-subtle text-brand' : 'text-fg hover:bg-surface-muted',
                  )}
                >
                  {command.group === t('palette.group.meetings') ? (
                    <CalendarClock aria-hidden className="h-4 w-4 shrink-0 opacity-60" />
                  ) : (
                    <ArrowRight aria-hidden className="h-4 w-4 shrink-0 opacity-60" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{command.label}</span>
                  {command.hint && <span className="shrink-0 text-xs text-fg-subtle">{command.hint}</span>}
                  {index === activeIndex && <CornerDownLeft aria-hidden className="h-3.5 w-3.5 shrink-0" />}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
